import type { ReactNode } from "react";

/**
 * Single 48 px glyph button used throughout the app header.
 *
 * Same visual treatment regardless of page — rounded-2xl glass, 1 px rim,
 * lime accent on hover. `tone` swaps the foreground colour for glyphs that
 * carry a severity of their own (billing warnings, errors).
 *
 * Keep this dumb on purpose: presentation only, no behaviour. The popover
 * trigger is a separate component that wraps this with extra a11y wiring.
 */
export function HeaderGlyph({
  label,
  onClick,
  children,
  tone,
  pressed,
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  tone?: "danger" | "warn" | "info";
  pressed?: boolean;
}) {
  // A tone outranks the pressed state: a warning glyph must keep signalling
  // its severity while its popover is open.
  const toneColor =
    tone === "danger"
      ? "var(--color-tactical-red)"
      : tone === "warn"
        ? "#fbbf24"
        : tone === "info"
          ? "#7dd3fc"
          : null;
  const color = toneColor ?? (pressed ? "var(--color-pitch-lime)" : "var(--color-on-surface)");

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      className="flex size-12 items-center justify-center rounded-2xl border transition-colors duration-fast"
      style={{
        borderColor: pressed ? "rgba(151,252,0,0.45)" : "rgba(255,255,255,0.10)",
        background: pressed ? "rgba(151,252,0,0.10)" : "rgba(255,255,255,0.04)",
        color,
      }}
    >
      {children}
    </button>
  );
}
